import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { useConfirmPhone, useGetNewCode } from 'src/components/AuthNew/hooks';
import { setPhoneFormat } from 'src/components/AuthNew/utils';
import { SmsCodeForm } from 'src/components/AuthNew/components/SmsCodeForm';
import type {
  SharedState,
  ConfirmPhoneConfig,
} from 'src/components/AuthNew/types';
import {
  AuthFormType,
  RequestStatus,
  PageType,
  ClientType,
} from 'src/components/AuthNew/types';
import { SendSmsCodeAgainForm } from 'src/components/AuthNew/components/SendSmsCodeAgainForm';
import { getAuthLayoutWithCSS } from 'src/components/AuthNew/components/AuthLayout';
import type { CLIENT, FullClientData } from '@lt/api/dist/types/client';
import { AuthNew } from 'src/components/AuthNew';
import { ConfirmPhoneForm } from 'src/components/AuthNew/components/ConfirmPhoneForm';
import { getClientType } from 'src/components/AuthNew/utils/getClientType';
import { useTranslation } from '@lt/localization';
import ReCaptchaModal from 'src/components/ReCaptchaModal';
import { ClientFormStep } from './types';
import { authLayoutCSS } from './styles';
import {
  MailConfirmation,
  RegistrationMailConfirmation,
  ClientUpdateForm,
} from './components';
import {
  useEmailWasChanged,
  useStateNewPhone,
  useClientFormStepState,
  useUpdateClient,
} from './hooks';
import { LOG_IN_ID } from '../../store/constants/tourists';

const baseCls = 'checkout-client';

type Props = {
  clientData: CLIENT | null;
  clientPhone: string;
  setClientInfo(clientData: FullClientData): void;
  setConfirmedPhone?: (confirmPhoneConfig: ConfirmPhoneConfig) => void;
  confirmClient(clientData: CLIENT): void;
  logIn(clientData: CLIENT): void;
  signUp(clientData: CLIENT): void;
  logOut(): void;
  isLoggedIn: boolean;
  isConfirmedEmail?: string | null;
  isMobile?: boolean;
  isWhitelabel?: boolean;
  clientAuthToken?: string;
  unconfirmedPhone?: string | undefined;
};

/**
 * @TODO: добавить в стейт клиента не подтвержденную почту
 * */
const Client = ({
  isLoggedIn,
  logIn,
  signUp,
  logOut,
  clientPhone,
  clientData,
  isConfirmedEmail,
  setConfirmedPhone,
  isMobile,
  isWhitelabel,
  confirmClient,
  setClientInfo,
  clientAuthToken,
  unconfirmedPhone,
}: Props) => {
  const initialSharedState = useMemo(
    () => ({
      id: clientData?.id,
      unconfirmed_phone: unconfirmedPhone,
      phone: clientData?.phone || '',
      phone_mask: '',
      email: clientData?.email || '',
      name: clientData?.name || '',
      surname: clientData?.surname || '',
      password: '',
      signUpType: null,
    }),
    [clientData, unconfirmedPhone],
  );

  const onGetClientType = getClientType(
    isLoggedIn,
    isConfirmedEmail,
    unconfirmedPhone,
  );

  const [sharedState, setSharedState] =
    useState<SharedState>(initialSharedState);

  useEffect(() => {
    setSharedState((prev) => ({
      ...initialSharedState,
      // не сбрасываем маску номера телефона
      phone_mask: prev.phone_mask,
    }));
  }, [initialSharedState]);

  const [requestStatus, setRequestStatus] = useState(RequestStatus.Idle);
  const {
    clientFormStep,
    openConfirmationPhone,
    openMailConfirmation,
    openClientInfo,
    openSendSmsCodeAgainForm,
    openLoginForm,
  } = useClientFormStepState(isLoggedIn);

  const [clientFormType, setClientFormType] =
    useState<ClientFormStep>(clientFormStep);

  useEffect(() => {
    setClientFormType(clientFormStep);
  }, [clientFormStep]);

  const { emailWasChangedRef, setEmailWasChanged } = useEmailWasChanged();
  const { newPhone, setNewPhone } = useStateNewPhone();

  const {
    timeLeftToDeliver,
    handleGetNewCode,
    timer,
    startTimer,
    isOpenReCaptcha,
    captchaCallback,
  } = useGetNewCode();

  const goToTourists = () => {
    if (!clientData) {
      return;
    }

    confirmClient(clientData);
    openClientInfo();
  };

  const { submitUpdateClient } = useUpdateClient({
    clientAuthToken,
    clientData,
    setClientInfo,
    setRequestStatus,
    setEmailWasChanged,
    setNewPhone,
    openMailConfirmation,
    openConfirmationPhone,
    confirmClient,
    handleGetNewCode,
  });

  const confirmFormFuncs = {
    onSuccessConfirmPhone: (authNewState?) => {
      if (emailWasChangedRef.current) {
        openMailConfirmation();
      } else {
        const clientDataWithNewPhone = {
          ...clientData,
          ...authNewState,
          phone:
            newPhone ||
            sharedState.unconfirmed_phone ||
            authNewState?.unconfirmed_phone,
          unconfirmed_phone: null,
          auth_token: clientAuthToken,
        } as FullClientData;

        openMailConfirmation();

        /** сохранение подтвержденного телефона в стор */
        setClientInfo(clientDataWithNewPhone);
      }
    },
    onSendSmsCodeAgain: openSendSmsCodeAgainForm,
    setRequestStatus,
  };

  const { handleConfirmPhone } = useConfirmPhone({
    onSuccessConfirmPhone: confirmFormFuncs.onSuccessConfirmPhone,
    onSendSmsCodeAgain: confirmFormFuncs.onSendSmsCodeAgain,
    setRequestStatus: confirmFormFuncs.setRequestStatus,
  });

  const AuthLayoutWithCSS = useMemo(
    () =>
      getAuthLayoutWithCSS(authLayoutCSS)(
        AuthFormType.LoginWithSMSCode,
        true,
        true,
        isMobile,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authLayoutCSS, isMobile],
  );
  const ClientUpdateLayout = useMemo(
    () =>
      getAuthLayoutWithCSS(authLayoutCSS)(
        AuthFormType.Registration,
        true,
        isMobile,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authLayoutCSS, isMobile],
  );

  const onSignIn = useCallback(
    (client) => {
      setTimeout(() => {
        if (!client.unconfirmed_phone) {
          logIn(client);
          openClientInfo();
        } else logIn(client);
      }, 2000);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [logIn],
  );

  useEffect(() => {
    setNewPhone(clientPhone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientPhone]);

  const backToLoginFormWithLogout = (value = '') => {
    if (typeof value === 'string') {
      if (/[a-zA-Zа-яА-яёЁ]/.test(value)) {
        setSharedState({ ...sharedState, phone: '', email: value });
      } else {
        setSharedState({ ...sharedState, phone: value, email: '' });
      }
    }
    openLoginForm();
    logOut();
  };

  const handleSetConfirmedPhone = useCallback(
    (confirmPhoneConfig) => {
      setTimeout(() => {
        if (setConfirmedPhone) setConfirmedPhone(confirmPhoneConfig);
        if (confirmPhoneConfig.shouldHideAuthPopup) openClientInfo();
      }, 2000);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setConfirmedPhone],
  );

  function initialFormType() {
    if (
      onGetClientType === ClientType.UnconfEmailUnconfPhone ||
      onGetClientType === ClientType.ConfEmailUnconfPhone
    ) {
      return AuthFormType.SetPhoneForConfirm;
    }
    return AuthFormType.Login;
  }
  const { t } = useTranslation();
  return (
    <div className={baseCls} id={LOG_IN_ID}>
      {clientFormType === ClientFormStep.AUTH && (
        <AuthNew
          isMobile={isMobile}
          isWhiteLabel={isWhitelabel}
          authLayoutCSS={authLayoutCSS}
          onSignIn={onSignIn}
          onSignUp={(data) => {
            signUp(data);
            startTimer();
            openConfirmationPhone();
          }}
          onLogOut={logOut}
          onClose={(data) => {
            if (isLoggedIn && data) {
              openClientInfo();
              /** переход к вводу данных о туристах */
              confirmClient(data);
            }
          }}
          showCloseButton
          CustomMailConfirmationForm={RegistrationMailConfirmation}
          onSuccessfulSignUpByPhone={confirmFormFuncs.onSuccessConfirmPhone}
          setConfirmedPhone={handleSetConfirmedPhone}
          pageType={PageType.Checkout}
          clientType={onGetClientType}
          initialFormType={initialFormType()}
          outsideSharedState={sharedState}
        />
      )}
      {clientFormType === ClientFormStep.CLIENT_INFO &&
        clientData &&
        (onGetClientType === ClientType.ConfEmailConfPhone ||
          onGetClientType === ClientType.UnconfEmailConfPhone) && (
          <ClientUpdateLayout>
            <ClientUpdateForm
              isMobile={isMobile}
              requestStatus={requestStatus}
              sharedState={sharedState}
              setSharedState={setSharedState}
              client={clientData}
              onSubmit={submitUpdateClient}
              backToLoginForm={backToLoginFormWithLogout}
            />
          </ClientUpdateLayout>
        )}

      {clientFormType === ClientFormStep.CLIENT_INFO &&
        (onGetClientType === ClientType.ConfEmailUnconfPhone ||
          onGetClientType === ClientType.UnconfEmailUnconfPhone) && (
          <AuthLayoutWithCSS>
            <ConfirmPhoneForm
              timeLeftToDeliver={timeLeftToDeliver}
              handleGetNewCode={handleGetNewCode}
              setSharedState={setSharedState}
              setFormType={openConfirmationPhone}
              setRequestStatus={setRequestStatus}
              sharedState={sharedState}
            />
          </AuthLayoutWithCSS>
        )}

      {clientFormType === ClientFormStep.PHONE_CONFIRMATION && (
        <AuthLayoutWithCSS onBack={openClientInfo}>
          <SmsCodeForm
            timeLeftToDeliver={timeLeftToDeliver}
            timer={timer}
            clientId={clientData?.id}
            onGetNewCode={handleGetNewCode}
            requestStatus={requestStatus}
            switchForm={() => null}
            phone={setPhoneFormat(
              sharedState.phone_mask ?? '',
              newPhone || sharedState.unconfirmed_phone || '',
            )}
            isConfirmPhoneProcess
            isAuthProcess={false}
            onConfirmPhone={handleConfirmPhone}
          />
        </AuthLayoutWithCSS>
      )}

      {clientFormType === ClientFormStep.SEND_SMS_CODE_AGAIN_FORM && (
        <AuthLayoutWithCSS onBack={openClientInfo}>
          <SendSmsCodeAgainForm
            description={t('checkout.smsCode')}
            backToStartHandler={openClientInfo}
            getNewCodeOnSuccess={openConfirmationPhone}
            onSubmit={async (config): Promise<void> => {
              handleGetNewCode(config);
              openConfirmationPhone();
            }}
            phone={setPhoneFormat(sharedState.phone_mask ?? '', newPhone ?? '')}
            isAuthProcess={false}
            clientId={clientData?.id}
          />
        </AuthLayoutWithCSS>
      )}

      {clientFormType === ClientFormStep.MAIL_CONFIRMATION && (
        <AuthLayoutWithCSS onClose={openClientInfo}>
          <MailConfirmation submitMailConfirmation={goToTourists} />
        </AuthLayoutWithCSS>
      )}
      <ReCaptchaModal
        isOpened={isOpenReCaptcha}
        onCaptchaPassed={captchaCallback}
        isCompact={isMobile}
      />
    </div>
  );
};

Client.defaultProps = {
  // eslint-disable-next-line react/default-props-match-prop-types
  clientData: null,
};

export default memo(Client);
